/**
 * Pure domain & mathematical logic for Matrix Hall Generator & Tile Coordinate Transformations.
 * (Ticket 06: Matrix Wizard & Tile Coordinate Inspector).
 * 
 * Standards:
 * - 1 Raised floor tile = 600 mm × 600 mm = 0.6 m × 0.6 m
 * - At PPM = 100, 1 tile = 60 px.
 */

import { applyMask, buildMaskVars } from './naming.ts';
import { formatNamingProfile, resolveNamingProfileVersion, type NamingProfileAssignments, type NamingProfileCatalog } from './namingProfile.logic.ts';
import type { Box2D } from './collisionGuard.logic.ts';
import { boxesIntersectOBB } from './collisionGuard.logic.ts';

export const PPM = 100;
export const TILE_M = 0.6;
export const TILE_PX = 60;

export const ROW_COLORS = [
  '#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ef4444',
  '#06b6d4', '#eab308', '#ec4899', '#14b8a6', '#f59e0b',
];

export interface MatrixHallParams {
  rowCount: number;
  racksPerRow: number;
  startTileX: number;
  startTileY: number;
  startRowIndex?: number;
  rackWidthM?: number;
  rackDepthM?: number;
  rackHeightM?: number;
  aisleWidthM?: number;
  rowSpacingM?: number;
  namingMask?: string;
  module?: string;
  hall?: string;
  namingProfiles?: NamingProfileCatalog;
  namingAssignments?: NamingProfileAssignments;
  firstRowOrientation?: number;
  alternateOrientation?: boolean;
  createAisles?: boolean;
  containment?: boolean;
  startSeq?: number;
}

export interface GeneratedRack {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  orientation: 0 | 90 | 180 | 270;
  label: string;
  rackType: 'server_rack';
  kind: 'rack';
  uCount: number;
  widthM: number;
  depthM: number;
  heightM: number;
  tenant: null;
  rowId: string;
  rackPosInRow: number;
  isDirty: boolean;
  rowIndex: number;
  rackIndex: number;
}

export interface GeneratedRow {
  id: string;
  name: string;
  labelIndex: number;
  orientation: number;
  color: string;
}

export interface GeneratedAisleZone {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  aisleType: 'cold' | 'hot' | 'containment';
  label: string;
  isDirty: boolean;
  isContained?: boolean;
}

export interface MatrixHallResult {
  racks: GeneratedRack[];
  rows: Record<string, GeneratedRow>;
  aisleZones: GeneratedAisleZone[];
  totalWidthM: number;
  totalDepthM: number;
  totalRacks: number;
  nextSeq: number;
}

export function metersToTiles(m: number, tileM: number = TILE_M): number {
  return Math.round(m / tileM);
}

export function tilesToMeters(tiles: number, tileM: number = TILE_M): number {
  return Number((tiles * tileM).toFixed(2));
}

export function pxToTiles(px: number, tilePx: number = TILE_PX): number {
  return Math.round(px / tilePx);
}

export function tilesToPx(tiles: number, tilePx: number = TILE_PX): number {
  return Math.round(tiles * tilePx);
}

export function rowIndexToLetter(index: number): string {
  let letter = '';
  let temp = index;
  while (temp >= 0) {
    letter = String.fromCharCode(65 + (temp % 26)) + letter;
    temp = Math.floor(temp / 26) - 1;
  }
  return letter;
}

function genId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Generates an entire hall matrix with rows, racks, and optional cold/hot aisles according to ASHRAE TC 9.9.
 */
export function generateMatrixHall(params: MatrixHallParams): MatrixHallResult {
  const rowCount = Math.max(1, Math.min(100, Math.round(params.rowCount || 1)));
  const racksPerRow = Math.max(1, Math.min(200, Math.round(params.racksPerRow || 1)));
  const startRowIndex = Math.max(0, Math.round(params.startRowIndex || 0));
  const rackWidthM = Math.max(0.4, Math.min(2.0, params.rackWidthM ?? 0.6));
  const rackDepthM = Math.max(0.5, Math.min(2.5, params.rackDepthM ?? 1.0));
  const rackHeightM = Math.max(1.0, Math.min(3.5, params.rackHeightM ?? 2.0));
  const aisleWidthM = Math.max(0.6, Math.min(5.0, params.aisleWidthM ?? 1.2));
  const rowSpacingM = Math.max(0, Math.min(5.0, params.rowSpacingM ?? 1.2));
  const namingMask = params.namingMask?.trim() || '{row_letter}-{rack:02d}';
  const module = params.module ?? '1';
  const hall = params.hall ?? 'A';
  const firstRowOrientation = (params.firstRowOrientation ?? 0) % 360;
  const alternateOrientation = params.alternateOrientation ?? true;
  const createAisles = params.createAisles ?? true;
  const containment = params.containment ?? false;

  const wPx = Math.round(rackWidthM * PPM);
  const dPx = Math.round(rackDepthM * PPM);
  const aislePx = Math.round(aisleWidthM * PPM);
  const rowSpacingPx = Math.round(rowSpacingM * PPM);

  const startX = tilesToPx(Math.max(0, Math.round(params.startTileX || 0)));
  const startY = tilesToPx(Math.max(0, Math.round(params.startTileY || 0)));

  const racks: GeneratedRack[] = [];
  const rows: Record<string, GeneratedRow> = {};
  const aisleZones: GeneratedAisleZone[] = [];
  let seq = params.startSeq ?? 10000;

  let currentY = startY;

  for (let ri = 0; ri < rowCount; ri++) {
    const rowId = genId();
    const actualRowIndex = startRowIndex + ri;
    const rowLetter = rowIndexToLetter(actualRowIndex);

    // Alternating orientation:
    // Even rows relative to start: firstRowOrientation (e.g. 0, front facing South/down)
    // Odd rows relative to start: (firstRowOrientation + 180) % 360 (front facing North/up)
    const isOdd = ri % 2 === 1;
    const rotation = alternateOrientation && isOdd
      ? (firstRowOrientation + 180) % 360
      : firstRowOrientation;

    rows[rowId] = {
      id: rowId,
      name: `Ряд ${rowLetter}`,
      labelIndex: actualRowIndex + 1,
      orientation: rotation,
      color: ROW_COLORS[actualRowIndex % ROW_COLORS.length],
    };

    // Place racks in row (baying with 0 gap along X)
    for (let rk = 0; rk < racksPerRow; rk++) {
      const rackNum = rk + 1;
      const maskVars = buildMaskVars({
        module,
        hall,
        row: actualRowIndex + 1,
        rack: rackNum,
      });
      const label = params.namingProfiles && params.namingAssignments
        ? formatNamingProfile(
          resolveNamingProfileVersion(params.namingProfiles, params.namingAssignments).profile,
          { module: Number(module), row: actualRowIndex + 1, rack: rackNum },
        )
        : applyMask(namingMask, maskVars);

      racks.push({
        id: genId(),
        x: startX + rk * wPx,
        y: currentY,
        width: wPx,
        height: dPx,
        rotation,
        orientation: rotation as (0 | 90 | 180 | 270),
        label,
        rackType: 'server_rack',
        kind: 'rack',
        uCount: 42,
        widthM: rackWidthM,
        depthM: rackDepthM,
        heightM: rackHeightM,
        tenant: null,
        rowId,
        rackPosInRow: rackNum,
        isDirty: true,
        rowIndex: 10000 + actualRowIndex,
        rackIndex: seq++,
      });
    }

    // Aisle zones between rows
    if (ri < rowCount - 1) {
      if (alternateOrientation) {
        // Physical check: does this row face towards row ri + 1 (South / 0°)?
        // If rotation === 0, front faces down (+Y) towards row ri + 1 -> Front-to-Front = Cold Aisle.
        // If rotation === 180, front faces up (-Y), back faces down (+Y) towards row ri + 1 -> Back-to-Back = Hot Aisle.
        const isFrontToFront = (rotation === 0);

        if (isFrontToFront) {
          if (createAisles) {
            aisleZones.push({
              id: genId(),
              x: startX,
              y: currentY + dPx,
              w: racksPerRow * wPx,
              h: aislePx,
              aisleType: containment ? 'containment' : 'cold',
              label: `Холодный коридор ${Math.floor(actualRowIndex / 2) + 1}`,
              isDirty: true,
              isContained: containment,
            });
          }
          currentY += dPx + aislePx;
        } else {
          if (createAisles && rowSpacingPx > 0) {
            aisleZones.push({
              id: genId(),
              x: startX,
              y: currentY + dPx,
              w: racksPerRow * wPx,
              h: rowSpacingPx,
              aisleType: 'hot',
              label: `Горячий коридор ${Math.floor(actualRowIndex / 2) + 1}`,
              isDirty: true,
            });
          }
          currentY += dPx + rowSpacingPx;
        }
      } else {
        // Uniform orientation -> standard service aisle
        if (createAisles) {
          aisleZones.push({
            id: genId(),
            x: startX,
            y: currentY + dPx,
            w: racksPerRow * wPx,
            h: aislePx,
            aisleType: 'cold',
            label: `Проход ${actualRowIndex + 1}`,
            isDirty: true,
          });
        }
        currentY += dPx + aislePx;
      }
    } else {
      currentY += dPx;
    }
  }

  const totalWidthM = Number(((racksPerRow * wPx) / PPM).toFixed(2));
  const totalDepthM = Number(((currentY - startY) / PPM).toFixed(2));

  return {
    racks,
    rows,
    aisleZones,
    totalWidthM,
    totalDepthM,
    totalRacks: racks.length,
    nextSeq: seq,
  };
}

/**
 * Calculates accurate tile spans along X and Y accounting for rack rotation (0/90/180/270 deg).
 * At 90/270 deg, width and depth swap on the floor grid.
 */
export function getRotatedTileSpan(
  widthM: number,
  depthM: number,
  rotation: number = 0,
  tileM: number = TILE_M
): { spanX: number; spanY: number; spanXStr: string; spanYStr: string } {
  const isTransposed = rotation === 90 || rotation === 270;
  const effectiveWidthM = isTransposed ? depthM : widthM;
  const effectiveDepthM = isTransposed ? widthM : depthM;

  const spanX = Number((effectiveWidthM / tileM).toFixed(1));
  const spanY = Number((effectiveDepthM / tileM).toFixed(1));

  return {
    spanX,
    spanY,
    spanXStr: spanX.toFixed(1),
    spanYStr: spanY.toFixed(1),
  };
}

/**
 * Checks a list of generated candidate racks against existing floor boxes.
 * Returns array of collisions with labels for warning the user.
 */
export function findMatrixCollisions(
  candidateRacks: Array<{ id: string; x: number; y: number; width: number; height: number; rotation: number; label: string }>,
  existingBoxes: Box2D[],
  existingLabels?: Record<string, string>
): Array<{ candidateLabel: string; existingLabel: string }> {
  const collisions: Array<{ candidateLabel: string; existingLabel: string }> = [];

  for (const cand of candidateRacks) {
    const candBox: Box2D = {
      id: cand.id,
      x: cand.x,
      y: cand.y,
      w: cand.width,
      h: cand.height,
      rotation: cand.rotation,
    };

    for (const ex of existingBoxes) {
      if (boxesIntersectOBB(candBox, ex)) {
        collisions.push({
          candidateLabel: cand.label,
          existingLabel: existingLabels?.[ex.id] || ex.id,
        });
        break;
      }
    }
  }

  return collisions;
}
